import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-[10px] text-sm font-medium transition-[background-color,color,transform,box-shadow] duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-500 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.97] motion-reduce:active:scale-100',
  {
    variants: {
      variant: {
        // Primary — deep sage gradient with a top-edge highlight ("light
        // catches the button"), pressed = solid primaryDark.
        default:
          'bg-gradient-to-b from-primary-500 to-primary-600 text-white [box-shadow:inset_0_1px_0_rgb(255_255_255/0.28),0_1px_2px_rgb(16_42_31/0.2)] hover:from-primary-600 hover:to-primary-600 active:from-primary-700 active:to-primary-700',
        // Accent — terracotta. Reserved for connect CTAs (Plaid, health) only.
        accent:
          'bg-accent-600 text-white shadow-sm hover:bg-accent-700 [box-shadow:inset_0_1px_0_rgb(255_255_255/0.22)]',
        destructive: 'bg-error-600 text-white shadow-sm hover:bg-error-700',
        // Destructive (soft) — tinted, for lower-stakes destructive actions.
        destructiveSoft: 'bg-error-50 text-error-600 hover:bg-error-100',
        outline: 'border border-gray-300 bg-surface shadow-sm hover:bg-gray-100 text-gray-900',
        // Secondary — soft sage tint, no hard outline (mobile primarySubtle).
        secondary: 'bg-primary-50 text-primary-700 hover:bg-primary-100',
        ghost: 'hover:bg-gray-100 text-gray-900',
        link: 'text-primary-600 underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-[10px] px-3 text-xs',
        lg: 'h-12 rounded-[14px] px-8 text-base',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
